import TextArea from "../../../Components/Common/TextArea/TextArea";
import { CHAR_LIMIT } from "../../../utils/Common/Constant";
import { useState } from "react";
import { useProgressReports } from "hooks/useProgressReports";
import ProgressSectionSkeleton from "Pages/Admin/ProjectDetail/sections/ProgressSectionSkeleton";
import { formatDate } from "utils/Common/Date";
import CustomModal from "Components/Common/CustomModal/CustomModal";
import Button from "Components/Common/Button/Button";
import { useToast } from "Components/Common/Toast/ToastContext";
import { postLenderDrawComment } from "services/lenderService";
import { useDocuments } from "hooks/useDocuments";
import { itemColor } from "utils/Common/ProgressBarColor";
import EmptyState from "Components/Common/EmptyState/EmptyState";
import NoProgressReports from "Assets/Img/NoProgressReports";

interface Props {
  projectId: string;
}

const formatEstimate = (val: string | null) => {
  if (!val) return "—";
  if (/[^\d.]/.test(val)) return `${val}%`;
  return `${parseFloat(val)}%`;
};

export default function BankProgressSection({ projectId }: Props) {
  const {
    reports,
    activeReportId,
    setActiveReportId,
    detail,
    loadingList,
    loadingDetail,
  } = useProgressReports(projectId);

  const [collapsedPhases, setCollapsedPhases] = useState<
    Record<string, boolean>
  >({});
  const togglePhase = (key: string) =>
    setCollapsedPhases((prev) => ({ ...prev, [key]: !prev[key] }));

  const [showCommentModal, setShowCommentModal] = useState(false);
  const [newComment, setNewComment] = useState("");
  const [postingComment, setPostingComment] = useState(false);
  const showToast = useToast();
  const { handleDownload, downloadingId } = useDocuments(projectId, false);

  const handlePostComment = async () => {
    const drawId = detail?.drawRequestId ?? detail?.drawRequest?.id;
    if (!projectId || !drawId || !newComment.trim()) return;
    setPostingComment(true);
    const res = await (postLenderDrawComment(
      projectId,
      drawId,
      newComment,
    ) as Promise<any>);
    setPostingComment(false);
    if (res.error) {
      showToast(res.error.message || "Failed to post comment.");
    } else {
      showToast("Comment posted.");
      setNewComment("");
      setShowCommentModal(false);
    }
  };

  if (loadingList) return <ProgressSectionSkeleton />;
  if (!reports.length)
    return (
      <EmptyState
        icon={<NoProgressReports />}
        title="No Progress Reports Yet"
        subtitle="Progress reports will appear here once inspections are completed and submitted."
      />
    );

  const lineItems = detail?.inspectionSnapshot?.lineItems ?? [];
  const phaseGroups = detail?.inspectionSnapshot?.phases ?? [];
  const isResidential = phaseGroups.length > 0;
  const photos = (detail?.documents ?? []).filter((d) => d.isIncluded);
  const reporterName = detail
    ? `${detail.reportBy.firstName} ${detail.reportBy.lastName}`.trim()
    : "";
  const gcPct = detail?.drawRequest?.gcBilledPct ?? null;
  const hasAnyFlag = isResidential
    ? phaseGroups.some((p) => p.items.some((item) => item.isFlagged))
    : lineItems.some((item) => item.isFlagged);

  return (
    <div>
      <div className="rtabs">
        {reports.map((r) => (
          <Button
            key={r.id}
            className={`rtab${activeReportId === r.id ? " active" : ""}`}
            onClick={() => setActiveReportId(r.id)}
          >
            Report {r.reportNumber} — {formatDate(r.reportDate, false)}
          </Button>
        ))}
      </div>

      {loadingDetail ? (
        <ProgressSectionSkeleton />
      ) : detail ? (
        <div className="card">
          <div className="card-title">
            Report {detail.reportNumber}
            {detail.inspectionDate &&
              ` — Inspection ${formatDate(detail.inspectionDate, false)}`}
            {` · ${formatDate(detail.reportDate, false)}`}
            <div style={{ display: "flex", gap: 6 }}>
              <Button
                className="btn btn-sm"
                onClick={() => setShowCommentModal(true)}
              >
                Comment
              </Button>
              <Button
                className="btn btn-sm"
                onClick={() =>
                  activeReportId &&
                  handleDownload(
                    {
                      id: activeReportId,
                      label: `ProgressReport-${activeReportId}.pdf`,
                    },
                    true,
                  )
                }
                disabled={downloadingId === activeReportId}
              >
                {downloadingId === activeReportId ? (
                  <>
                    <span className="btn-spinner" /> Downloading…
                  </>
                ) : (
                  "PDF"
                )}
              </Button>
            </div>
          </div>

          <div className="rpt-metrics">
            <div className="rpt-metric">
              <div className="rpt-metric-lbl">CSC Estimate</div>
              <div className="rpt-metric-val amber">
                {formatEstimate(detail.cscEstimation)}
              </div>
            </div>
            <div className={`rpt-metric${hasAnyFlag ? " warn" : ""}`}>
              <div className="rpt-metric-lbl">GC Billed</div>
              <div className={`rpt-metric-val${hasAnyFlag ? " red" : ""}`}>
                {gcPct != null ? `${gcPct}%` : "—"}
                {hasAnyFlag && " ⚠"}
              </div>
            </div>
            <div className="rpt-metric">
              <div className="rpt-metric-lbl">Draw Amount</div>
              <div className="rpt-metric-val">
                {detail.drawRequest?.requestedAmount?.amount != null
                  ? `$${detail.drawRequest.requestedAmount.amount.toLocaleString()}`
                  : "—"}
              </div>
            </div>
          </div>

          {/* <div className="rpt-sec-lbl">Progress by Phase</div> */}
          {isResidential
            ? phaseGroups.map((phase) => {
                const isCollapsed = Boolean(collapsedPhases[phase.phase]);
                const phaseColor = itemColor(phase.phaseCscEstimation);
                return (
                  <div className="bnk-phase-section" key={phase.phase}>
                    <div
                      className={`bnk-phase-header${isCollapsed ? " collapsed" : ""}`}
                      onClick={() => togglePhase(phase.phase)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ")
                          togglePhase(phase.phase);
                      }}
                    >
                      <div className="bnk-ph-left">
                        <span className="bnk-ph-label">
                          Phase {phase.phase}
                        </span>
                        <span className="bnk-ph-weight">
                          {phase.phaseWeight}% of total
                        </span>
                      </div>
                      <div className="bnk-ph-right">
                        <span
                          className="bnk-ph-pct"
                          style={{ color: phaseColor }}
                        >
                          {phase.phaseCscEstimation.toFixed(0)}%
                        </span>
                        <span className="bnk-ph-chevron">▼</span>
                      </div>
                    </div>
                    <div
                      className={`bnk-phase-body${isCollapsed ? " collapsed" : ""}`}
                    >
                      {phase.items.map((item) => {
                        const pct = item.cscEstimation ?? 0;
                        const noteEntries = item.note
                          ? Object.entries(item.note)
                              .filter(
                                ([k]) => k !== "flag" && !isNaN(Number(k)),
                              )
                              .map(([, v]) => v as string)
                          : [];
                        return (
                          <div
                            className={`bnk-fi-item${item.isFlagged ? " flagged" : ""}`}
                            key={item.id}
                          >
                            <div className="bnk-fi-top">
                              <span className="bnk-fi-code">
                                {item.itemCode}
                              </span>
                              <span className="bnk-fi-label">
                                {item.itemLabel ?? "—"}
                              </span>
                              <span
                                className="bnk-fi-pct"
                                style={{
                                  color: itemColor(pct, item.isFlagged),
                                }}
                              >
                                {pct}%{item.isFlagged && " ⚠"}
                              </span>
                            </div>
                            <div className="phase-bar">
                              <div
                                className="phase-fill"
                                style={{
                                  width: `${pct}%`,
                                  background: itemColor(pct, item.isFlagged),
                                }}
                              />
                            </div>
                            {noteEntries.map((note, i) => (
                              <div className="note-li" key={i}>
                                {note}
                              </div>
                            ))}
                            {item.isFlagged && item.note?.flag && (
                              <div className="flag-note">{item.note.flag}</div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })
            : lineItems.map((item) => {
                const pct = item.cscEstimation ?? 0;
                const billed = item.gcBilledPct ?? 0;
                const color = itemColor(pct, item.isFlagged);
                const noteEntries = item.note
                  ? Object.entries(item.note)
                      .filter(([k]) => k !== "flag" && !isNaN(Number(k)))
                      .map(([, v]) => v)
                  : [];
                return (
                  <div key={item.id} style={{ marginBottom: 10 }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        fontSize: 12,
                        fontFamily: "system-ui, sans-serif",
                      }}
                    >
                      <span style={{ color: "var(--tx2)" }}>
                        {item.aiaItem?.itemName ?? "—"}
                      </span>
                      <span style={{ fontWeight: 700, color }}>
                        {pct}%{item.isFlagged && ` (billed ${billed}%) ⚠`}
                      </span>
                    </div>
                    <div className="phase-bar">
                      <div
                        className="phase-fill"
                        style={{ width: `${pct}%`, background: color }}
                      />
                    </div>
                    {noteEntries.map((note, i) => (
                      <div className="note-li" key={i}>
                        {note}
                      </div>
                    ))}
                    {item.isFlagged && item.note?.flag && (
                      <div className="flag-note">{item.note.flag}</div>
                    )}
                  </div>
                );
              })}

          {photos.length > 0 && (
            <>
              <div className="rpt-sec-lbl" style={{ margin: "16px 0 10px" }}>
                Progress Photos —{" "}
                {formatDate(detail.inspectionDate ?? detail.reportDate, false)}
              </div>
              <div className="photo-grid">
                {photos.map((p, i) => (
                  <div className="photo-card" key={p.id}>
                    {p.document?.url ? (
                      <img
                        src={p.document.url}
                        alt={p.description ?? p.note ?? ""}
                      />
                    ) : (
                      <div className="photo-ph">
                        <div className="photo-num">{i + 1}</div>
                      </div>
                    )}
                    {p.description ??
                      (p.note && (
                        <div className="photo-cap">
                          {p.description ?? p.note}
                        </div>
                      ))}
                  </div>
                ))}
              </div>
            </>
          )}

          {detail.recommendationText && (
            <div className="csc-rec">
              <strong>CSC Recommendation:</strong> {detail.recommendationText}
            </div>
          )}

          <CustomModal
            show={showCommentModal}
            handleClose={() => {
              setShowCommentModal(false);
              setNewComment("");
            }}
            title="Add Comment"
            isHeaderVisible={true}
            isCrossBtn={false}
            isFooterVisible={true}
            footerContent={
              <>
                <Button
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowCommentModal(false);
                    setNewComment("");
                  }}
                  disabled={postingComment}
                >
                  Cancel
                </Button>
                <Button
                  className="approve-btn"
                  onClick={handlePostComment}
                  disabled={postingComment || !newComment.trim()}
                >
                  {postingComment ? "Posting…" : "Post Comment"}
                </Button>
              </>
            }
          >
            <p className="modal-sub">
              Your comment will be visible to CSC New England and logged against
              this project.
            </p>
            <TextArea
              className="form-control mt-3"
              placeholder="Type your comment…"
              rows={4}
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              disabled={postingComment}
              maxChars={CHAR_LIMIT.DESCRIPTION}
            />
          </CustomModal>

          <div className="rpt-footer">
            {reporterName && (
              <>
                Report by: <strong>{reporterName}</strong>&nbsp;·&nbsp;
              </>
            )}
            {formatDate(detail.reportDate, false)}
            {detail.invoiceAmount?.amount != null && (
              <>
                &nbsp;·&nbsp;Invoice: $
                {detail.invoiceAmount.amount.toLocaleString()}
              </>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
